// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ApiErrorPayloadCommon } from '@imprivata-cloud/common';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiClient } from '../client';

interface GetTenantResponse {
  name: string;
  logoUrl: string;
}

const getTenantQueryKey = 'get-tenant';

const getTenantQuery = async (): Promise<GetTenantResponse> => {
  const response = await apiClient.get<GetTenantResponse>('tenant');
  return response;
};

/**
 * Custom hook to get the tenant name
 *
 * NOTE: If the tenant name cannot be retrieved from the API, the hook will
 * fallback to the previous tenant name stored in local storage or default
 * to "Patient Access "
 *
 * @returns {Object} - An object containing the tenant name and a refetch function.
 */
export const useGetTenantQuery = () => {
  const tenantNameLocalStorageKey = 'TENANT_NAME';
  const tenantLogoUrlLocalStorageKey = 'TENANT_LOGO_URL';
  const fallbackName = 'Patient Access';

  const [tenantName, setTenantName] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string>('');

  const query = useQuery<GetTenantResponse, ApiErrorPayloadCommon>({
    queryKey: [getTenantQueryKey],
    queryFn: getTenantQuery,
    retry: false
  });

  useEffect(() => {
    const localTenantName = localStorage.getItem(tenantNameLocalStorageKey);
    const localLogoUrl = localStorage.getItem(tenantLogoUrlLocalStorageKey);
    if (localTenantName) {
      setTenantName(localTenantName);
    }
    if (localLogoUrl) {
      setLogoUrl(localLogoUrl);
    }
  }, []);

  useEffect(() => {
    if (query.isSuccess) {
      if (query.data) {
        if (query.data.name) {
          localStorage.setItem(tenantNameLocalStorageKey, query.data.name);
          setTenantName(query.data.name);
        } else {
          setTenantName(fallbackName);
        }
        if (query.data.logoUrl) {
          localStorage.setItem(tenantLogoUrlLocalStorageKey, query.data.logoUrl);
          setLogoUrl(query.data.logoUrl);
        } else {
          localStorage.removeItem(tenantLogoUrlLocalStorageKey);
          setLogoUrl('');
        }
      } else {
        setTenantName(fallbackName);
        setLogoUrl('');
      }
    }
  }, [query.data, query.isSuccess]);

  useEffect(() => {
    if (query.isError) {
      const localTenantName = localStorage.getItem(tenantNameLocalStorageKey);
      if (!localTenantName) {
        localStorage.setItem(tenantNameLocalStorageKey, fallbackName);
        setTenantName(fallbackName);
      } else {
        setTenantName(localTenantName);
      }
    }
  }, [query.isError]);

  return {
    tenantName,
    logoUrl,
    refetch: query.refetch,
    error: query.error,
  };
};
